// React Imports
import { useCallback, useEffect } from "react";

import "./AlgoManagement.styles.scss";

// Components Imports
import { AlgoTable, InteractionsModal, Skeleton, StartStrategyModal } from "./components";

// Hooks Imports
import { useInteractionsModal, useStartStrategyModal } from "./hooks";
import { useFetchInteractionsQuery } from "./services/algoManagementApi";

// Material UI Imports
import { Button, SvgIcon, useTheme } from "@mui/material";

// SVG Imports
import LogoSVG from "@/svgs/pulse_prime_logo.svg?react";
import { useAlerts } from "@/hooks";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { IAGAlgoDataRow } from "./components/AlgoTable/AlgoTable.types";

const AlgoManagement = () => {
    // Hooks - START ---------------------------------------------------------------
    const theme = useTheme();
    const { showError } = useAlerts();
    const { data: interactionsData, isLoading: isLoadingInteractionsData, isError: isErrorInteractionsData, error: errorInteractionsData } = useFetchInteractionsQuery();
    const { open: interactionsModalOpen, interaction, selectedBotData, connectionId, initialRpcName, selectedRowStatus, closeModal: closeInteractionsModal, openModal: openInteractionsModal } = useInteractionsModal();
    const { open: startStrategyModalOpen, interaction: startStrategyInteraction, openModal: openStartStrategyModal, closeModal: closeStartStrategyModal } = useStartStrategyModal();
    // Hooks - END -----------------------------------------------------------------

    const handleRowInteract = useCallback(
        (row: IAGAlgoDataRow) => {
            if (!interactionsData) return;
            openInteractionsModal(interactionsData, row.original_data, row.connection_id, {
                rowStatus: row.status,
            });
        },
        [interactionsData, openInteractionsModal]
    );

    const handleRowInteractToRpc = useCallback(
        (row: IAGAlgoDataRow, rpcName: string) => {
            if (!interactionsData) return;
            openInteractionsModal(interactionsData, row.original_data, row.connection_id, {
                initialRpcName: rpcName,
                rowStatus: row.status,
            });
        },
        [interactionsData, openInteractionsModal]
    );

    useEffect(() => {
        if (isErrorInteractionsData) {
            showError(`Failed to fetch Interactions: ${getErrorMessage(errorInteractionsData)}`);
        }
    }, [isErrorInteractionsData, errorInteractionsData, showError])

    return (
        <div className="pulse-sidenav-adjusted-container" style={{ backgroundColor: theme.palette.bg["900"] }}>
            <SvgIcon
                sx={{
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    top: 0,
                    left: 0,
                }}
                children={<LogoSVG style={{ color: theme.palette.bg[800] }} />}
            />

            <p className="pulse-typography-headline-6 pulse-color--background-50 pulse-row pulse-mb-1">
                Algo Management
            </p>

            {isLoadingInteractionsData ? <Skeleton /> :
                <>
                    <div className="pulse-row pulse-mb-1" style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                        <Button
                            variant="contained"
                            sx={{
                                color: theme.palette.bg["50"],
                                backgroundColor: (theme.palette.success as unknown as Record<string, string>)["800"],
                                "&:hover": {
                                    backgroundColor: (theme.palette.success as unknown as Record<string, string>)["700"],
                                },
                            }}
                            onClick={() => { openStartStrategyModal(interactionsData); }}
                        >
                            Start Strategy
                        </Button>
                    </div>

                    <AlgoTable
                        onInteract={handleRowInteract}
                        interactionsData={interactionsData ?? undefined}
                        onInteractToRpc={handleRowInteractToRpc}
                    />
                    <StartStrategyModal
                        open={startStrategyModalOpen}
                        interaction={startStrategyInteraction}
                        handleClose={closeStartStrategyModal}
                        onRpcSuccess={closeStartStrategyModal}
                    />
                    <InteractionsModal
                        open={interactionsModalOpen}
                        interaction={interaction}
                        selectedBotData={selectedBotData}
                        connectionId={connectionId}
                        initialRpcName={initialRpcName}
                        selectedRowStatus={selectedRowStatus}
                        handleClose={closeInteractionsModal}
                        onRpcSuccess={closeInteractionsModal}
                    />
                </>}
        </div>
    );
};

export default AlgoManagement;
